import { BaseResponse, GenericResponse, ListResponse } from "@/common/common_utils";
import { axiosInstance } from "@/services/axios.service";
import { ENDPOINTS } from "@/constants/endpoints";
import { BillingInfoChangeRequestDto, PaymentCardDto, PaymentMethodChangeRequestDto } from "./billing.types";
import { showError } from "../notification/engine/notification.library";

export interface IdNameDto {
	Id: number;
	Name: string;
}

export interface BillingInfoDto {
	LegalName: string;
	Address1: string;
	Address2?: string;
	Province: IdNameDto;
	City: IdNameDto;
	PostalCode: string;
	Tin?: string;
}

export interface UpdateBillingInfoRequest {
	CityId: number;
	LegalName: string;
	Address1: string;
	Address2?: string;
	PostalCode: string;
	Tin: string;
}

export const BillingApi = {
	getProvinces: async (): Promise<IdNameDto[]> => {
		const response = await axiosInstance.post<GenericResponse<IdNameDto[]>>(ENDPOINTS['/api/X4bBilling/GetProvinces']);
		return response.data.Data;
	},
	getCities: async (provinceId: number): Promise<IdNameDto[]> => {
		console.log(provinceId);
		const response = await axiosInstance.post<GenericResponse<IdNameDto[]>>(
			ENDPOINTS['/api/X4bBilling/GetCities'],
			{ ProvinceId: provinceId },
		);
		return response.data.Data;
	},
	getBillingInfo: async (): Promise<BillingInfoDto> => {
		const response = await axiosInstance.post<GenericResponse<BillingInfoDto>>(ENDPOINTS['/api/X4bBilling/GetBillingInfo']);
		return response.data.Data;
	},
	updateBillingInfo: async (data: BillingInfoDto): Promise<BaseResponse> => {
		const request: UpdateBillingInfoRequest = {
			CityId: data.City!.Id,
			LegalName: data.LegalName,
			Address1: data.Address1,
			Address2: data.Address2,
			PostalCode: data.PostalCode,
			Tin: data.Tin ?? '',
		};

		const response = await axiosInstance.post<BaseResponse>(ENDPOINTS['/api/X4bBilling/UpdateBillingInfo'], request);
		return response.data;
	},

	// Billing Info Change Request methods
	getBillingInfoRequests: async (): Promise<BillingInfoChangeRequestDto[]> => {
		const response = await axiosInstance.post<GenericResponse<ListResponse<BillingInfoChangeRequestDto>>>(
			ENDPOINTS['/api/X4bBilling/GetBillingInfoRequests'],
			{}
		);
		return response.data.Data.Items;
	},

	approveBillingInfoRequest: async (requestId: number): Promise<BaseResponse> => {
		const response = await axiosInstance.post<BaseResponse>(
			ENDPOINTS['/api/X4bBilling/ApproveBillingInfoRequest'],
			{ Id: requestId }
		);
		return response.data;
	},

	declineBillingInfoRequest: async (requestId: number): Promise<BaseResponse> => {
		const response = await axiosInstance.post<BaseResponse>(
			ENDPOINTS['/api/X4bBilling/DeclineBillingInfoRequest'],
			{ Id: requestId }
		);
		return response.data;
	},

	// Payment Method Change Request methods
	getPaymentMethodRequests: async (): Promise<PaymentMethodChangeRequestDto[]> => {
		const response = await axiosInstance.post<GenericResponse<ListResponse<PaymentMethodChangeRequestDto>>>(
			ENDPOINTS['/api/payment-cards/GetPaymentMethodRequests'],
			{}
		);
		return response.data.Data.Items;
	},

	approvePaymentMethodRequest: async (requestId: number): Promise<BaseResponse> => {
		const response = await axiosInstance.post<BaseResponse>(
			ENDPOINTS['/api/payment-cards/ApprovePaymentMethodRequest'],
			{ Id: requestId }
		);
		return response.data;
	},

	declinePaymentMethodRequest: async (requestId: number): Promise<BaseResponse> => {
		const response = await axiosInstance.post<BaseResponse>(
			ENDPOINTS['/api/payment-cards/DeclinePaymentMethodRequest'],
			{ Id: requestId }
		);
		return response.data;
	},

	getXenditConfig: async (): Promise<{ PublicKey: string; XenPlatformAccountId: string; VerificationAmount: number }> => {
		const response = await axiosInstance.post<GenericResponse<{ PublicKey: string; XenPlatformAccountId: string; VerificationAmount: number }>>(
			ENDPOINTS['/api/payment-cards/GetXenditConfig'],
			{}
		);
		return response.data.Data;
	},

	createPaymentMethodChangeRequest: async (request: {
		OldCardId?: number;
		CardDetails: {
			TokenId: string;
			Brand?: string;
			MaskedCardNumber?: string;
			ExpMonth?: number;
			ExpYear?: number;
		}
	}): Promise<GenericResponse<{
		RequestId: number;
		CardId?: number;
		Status: string;
	}>> => {
		try {
			const response = await axiosInstance.post<GenericResponse<{
				RequestId: number;
				CardId?: number;
				Status: string;
			}>>(
				ENDPOINTS['/api/payment-cards/CreatePaymentMethodChangeRequest'],
				request,
				{ skipGenericErrorHandler: true }
			);
			return response.data;
		} catch (e: any) {
			const errorMessage = e?.response?.data?.ErrorMessage;
			showError(errorMessage || "An error occurred while processing your request.");
			throw e;
		}
	},

	// Payment Cards methods
	getPaymentCards: async (): Promise<PaymentCardDto[]> => {
		const response = await axiosInstance.post<GenericResponse<PaymentCardDto[]>>(
			ENDPOINTS['/api/payment-cards/GetList'],
			{}
		);
		return response.data.Data;
	},

	setDefaultCard: async (cardId: number): Promise<BaseResponse> => {
		const response = await axiosInstance.post<BaseResponse>(
			ENDPOINTS['/api/payment-cards/SetDefault'],
			{ Id: cardId }
		);
		return response.data;
	},

	deletePaymentCard: async (cardId: number): Promise<BaseResponse> => {
		const response = await axiosInstance.post<BaseResponse>(
			ENDPOINTS['/api/payment-cards/Delete'],
			{ Id: cardId }
		);
		return response.data;
	}
}


